import { ChangeEvent, useContext, useEffect, useState } from "react";
import Post, { PostContent, Table } from "../models/post";
import AddPostContentModal from "../views/add-post-content-modal";
import Divider from "../components/divider";
import TextInput from "../components/text-input";
import Row from "../components/row";
import { useRecoilValue } from "recoil";
import userState from "../utils/user-state";
import PrimaryButton from "../components/primary-button";
import SpaceBetween from "../components/space-between";
import Textarea from "../components/textarea";
import FirebaseContext from "../utils/firebase-context";
import { useHistory, useParams } from "react-router-dom";
import ContentComponent from "../views/content-component";
// @ts-ignore
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore
import BalloonEditor from '@ckeditor/ckeditor5-build-balloon';
import {
    AddContentButton,
    Input,
    Label,
    Placeholder,
    SubTopBar,
    TitleImage,
    TitleInput,
    Toggle,
    TopBarContent
} from "../views/post-components";
import getImageFromUrl from "../utils/get-image-from-url";
import DeleteButton from "../components/delete-button";
import { LinkListComponent } from "../views/link-list-component";
import { Loading } from "../views/loading";
import { AddRounded } from "@mui/icons-material";
import { nanoid } from "nanoid";
import styled from "styled-components";

const BannerWrapper = styled.div`
    position: relative;
`;

function ColorPicker(props: {color: string, onClick: () => void}) {
    return <div style={{
        backgroundColor: props.color,
        borderRadius: "6px",
        position: "absolute",
        top: "0.5rem",
        right: "0.5rem",
        bottom: "0.5rem",
        width: "2rem",
        cursor: "pointer",
    }} onClick={props.onClick} />
}

export default function EditPostScreen() {
    const [showModal, setShowModal] = useState(false);
    const [content, setContent] = useState<PostContent[]>([]); // Content of the article
    const [activeIndex, setActiveIndex] = useState(0);
    const user = useRecoilValue(userState); // Currently signed in user
    const [title, setTitle] = useState(""); // Post Title
    const [author, setAuthor] = useState(user ? user.firstName + " " + user.lastName : ""); // Author Name
    const [image, setImage] = useState<File>(); // Header Image
    const [excerpt, setExcerpt] = useState(""); // Summary
    const [time, setTime] = useState(""); // reading time
    const firebase = useContext(FirebaseContext);
    const { handle, id } = useParams<{ handle: string, id: string }>(); // handle is category name, firebase id
    const [published, setPublished] = useState(false); // article is live or not
    const [imageChanged, setImageChanged] = useState(false);
    const [sources, setSources] = useState<{ text: string; link: string }[]>(); // sources of the article
    const [banner, setBanner] = useState({id: 0, text: "", color: "red"});
    const [post, setPost] = useState<Post>(); 
    const history = useHistory();

    const [loading, setLoading] = useState(true);

    // post gets pulled from firestore and initial values are assigned
    useEffect(() => {
        setLoading(true);
        return firebase.db.collection(handle).doc(id).onSnapshot(async doc => {
            const post = doc.data() as Post;
            post.content = post.content.map(c => {
                c.key = nanoid();
                if (c.type === 'table') {
                    let columns = (c.value as Table).columns.map(cell => Object.values(cell)) as string[][];
                    return {...c, value: {...c.value, columns}} as PostContent;;
                } else {
                    return c;
                }
            });
            
            if (!post) return history.push(`/blogs/${handle}`);
            setPost(post);
            if (!!post.banner) {
                if (!post.banner.id) {
                    setBanner({...post.banner, id: 0});
                } else {
                    setBanner(post.banner);
                }
            } else {
                setBanner({id: 0, text: "", color: "red"});
            }
            setTitle(post.title);
            setAuthor(post.author);
            setImage(post.image ? await getImageFromUrl(post.image) : undefined);
            setContent(post.content);
            setExcerpt(post.excerpt);
            setTime(post.time);
            setPublished(post.published);
            setSources(post.sources ? (post.sources.length === 0 ? undefined : post.sources) : undefined);
            setLoading(false);
        })
    }, [firebase, handle, id, history]);

    const closeModal = () => {
        setShowModal(false);
    }

    const addContent = (c: PostContent, index: number) => {
        setContent(content => {
            const copy = [...content];
            copy.splice(index + 1, 0, c);
            return copy;
        });
        closeModal();
    };

    const addContentBefore = (index: number) => () => {
        openModal(index - 1);
    };
    const addContentAfter = (index: number) => () => {
        openModal(index);
    };

    const deleteContent = (index: number) => () => {
        let copy = [...content];
        copy.splice(index, 1);
        setContent(copy);
    }

    const openModal = (index: number) => {
        setActiveIndex(index);
        setShowModal(true);
    };

    const updateTitleImage = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !files[0]) return;
        setImage(files[0]);
        setImageChanged(true);
    };

    const changeColor = () => {
        switch (banner.color) {
            case "#FF7F7D":
            case 'red':
                setBanner(b => ({...b, color: "#E8EDD6"}));
                break;
            case "#E8EDD6":
                setBanner(b => ({...b, color: "#FFD86F"}));
                break;
            case "#FFD86F":
                setBanner(b => ({...b, color: "#7AD3FF"}));
                break;
            case "#7AD3FF":
                setBanner(b => ({...b, color: "#FF7F7D"}));
                break;
        }
    };

    const createPost = async () => { // should be updatePost, updates the post inside firebase
        if (!image || !post) return;
        setLoading(true);
        const p: Post = {
            ...post,
            title,
            author,
            excerpt,
            content,
            banner,
            published,
            time,
            sources: sources || [],
            blog: handle,
            id
        }
        await firebase.updateBlogPost(p, image, imageChanged);
        setLoading(false);
        history.go(0);
    }

    const deletePost = async () => {
        if (!post) return;
        setLoading(true);
        await firebase.deletePost(post);
        history.push("/blogs/" + handle);
    };

    return <>
        <SubTopBar>
            <TopBarContent>
                <TitleInput value={title} onChange={e => setTitle(e.target.value)} placeholder="Titel..." />
                <Toggle
                    value={published}
                    onClick={() => setPublished(p => !p)}
                    label="Veröffentlicht?" />
            </TopBarContent>
        </SubTopBar>
        <div style={{ height: "70px" }} />
        <div style={{
            display: "grid",
            gap: "1rem",
        }}>
            <TitleImage>
                {image ? <img src={URL.createObjectURL(image)} alt="" /> : <Placeholder>
                    <AddRounded />
                </Placeholder>}
                <Input type="file" id={`title-image-input`} onChange={updateTitleImage} accept={"image/*"} />
                <Label htmlFor={`title-image-input`} />
            </TitleImage>
            <Row>
                <Textarea value={excerpt} placeholder="Zusammenfassung" onChange={e => setExcerpt(e.target.value)} />
            </Row>
            <BannerWrapper>
                <Row>
                <CKEditor editor={BalloonEditor} data={banner.text} config={{
                    toolbar: ['bold', 'italic', 'link'],
                    placeholder: "Text schreiben..."
                    }} onChange={(_event: any, editor: { getData: any; }) => {
                        setBanner(b => ({...b, text: editor.getData()}))
                    }} />
                    
                </Row>
                <ColorPicker color={banner.color} onClick={changeColor} />
            </BannerWrapper>
            <Row>
                <TextInput value={author} onChange={e => setAuthor(e.target.value)} placeholder="Autor" />
                <TextInput value={time} onChange={e => setTime(e.target.value)} placeholder="Lesedauer in Minuten" />
            </Row>
            <Divider />
            {content.map((c, i) => {
                return <ContentComponent
                    key={c.key}
                    content={c}
                    index={i}
                    setContent={setContent}
                    deleteContent={deleteContent(i)}
                    onAfterAdd={addContentAfter(i)}
                    onBeforeAdd={addContentBefore(i)}
                />
            })}
        </div>

        <AddContentButton onClick={() => openModal(content.length - 1)}>
            Inhalt hinzufügen
        </AddContentButton>

        {!sources ? <AddContentButton onClick={() => setSources([])}>
            Quellen hinzufügen
        </AddContentButton> : <div style={{
            marginTop: "1rem"
        }}
        >
            <LinkListComponent
                deleteContent={() => setSources(undefined)}
                update={sources => setSources(sources)}
                linkList={{ items: sources! }}
            />
        </div>}

        <SpaceBetween style={{ marginTop: "1rem" }}>
            <DeleteButton onClick={deletePost}>
                Post Löschen
            </DeleteButton>
            <PrimaryButton onClick={createPost}>
                Speichern
            </PrimaryButton>
        </SpaceBetween>

        <div style={{ height: "10vh" }} />
        {showModal && <AddPostContentModal
            addContent={addContent}
            close={closeModal}
            index={activeIndex}
        />}
        {loading && <Loading />}
    </>
}
